<template>
  <div>
    <!-- Header / new bot button -->
    <b-card
      no-body
      class="mb-2"
    >
      <b-card-body class="d-flex align-items-center py-1">
        <h4 class="mb-0">
          <feather-icon
            icon="CpuIcon"
            size="20"
            class="text-primary mr-1"
          />
          Botlar ({{ metaData.total }})
        </h4>
        <b-button
          variant="primary"
          class="ml-auto"
          @click="openCreateModal"
        >
          <feather-icon icon="PlusIcon" /> Yeni Bot
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Filter toolbar -->
    <b-card
      no-body
      class="mb-2"
    >
      <b-card-body class="py-1">
        <b-row>
          <b-col
            cols="6"
            md="3"
          ><label class="font-small-3 text-muted">Durum</label>
            <b-form-select
              v-model="filter.status"
              :options="statusOptions"
              @change="fetchList"
            />
          </b-col>
          <b-col
            cols="6"
            md="3"
          ><label class="font-small-3 text-muted">Mod</label>
            <b-form-select
              v-model="filter.mode"
              :options="modeOptions"
              @change="fetchList"
            />
          </b-col>
          <b-col
            cols="6"
            md="3"
          ><label class="font-small-3 text-muted">Market</label>
            <b-form-select
              v-model="filter.market"
              :options="marketOptions"
              @change="fetchList"
            />
          </b-col>
          <b-col
            cols="6"
            md="3"
          ><label class="font-small-3 text-muted">Ad / Sembol</label>
            <b-form-input
              v-model="filter.search"
              placeholder="ara..."
              @input="onSearchInput"
            />
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Bot table -->
    <b-card no-body>
      <b-table
        responsive="sm"
        :fields="fields"
        :items="dataList"
        :busy="loading"
        striped
        show-empty
        empty-text="Henüz bot oluşturulmadı. 'Yeni Bot' ile başlayın."
      >
        <template #cell(name)="data">
          <div class="font-weight-bold">
            {{ data.item.name }}
          </div>
          <div class="font-small-3 text-muted">
            <b-badge
              :variant="modeVariant(data.item.mode)"
              class="mr-1"
            >
              {{ data.item.mode }}
            </b-badge>
            {{ data.item.market }} · {{ data.item.symbol || '—' }} · {{ data.item.timeframe || '—' }}
          </div>
        </template>
        <template #cell(indicator)="data">
          <div
            v-if="data.item.indicator"
            class="font-small-3"
          >
            {{ data.item.indicator.name }}
          </div>
          <div
            v-if="data.item.indicator"
            class="font-small-2 text-muted"
          >
            {{ data.item.indicator.engineKey }}
          </div>
        </template>
        <template #cell(tpsl)="data">
          <div class="font-small-3">
            TP {{ formatNum(data.item.tpPerc, 2) }}% / SL {{ formatNum(data.item.slPerc, 2) }}%
          </div>
          <div class="font-small-2 text-muted">
            Lev {{ formatNum(data.item.leverage, 1) }}x · Kom {{ formatNum(data.item.commissionPerSide, 3) }}%
          </div>
        </template>
        <template #cell(pnl)="data">
          <div
            :class="(Number(data.item.realizedPnl) || 0) >= 0 ? 'text-success' : 'text-danger'"
            class="font-weight-bold"
          >
            {{ (Number(data.item.realizedPnl) || 0) >= 0 ? '+' : '' }}{{ formatNum(data.item.realizedPnl, 4) }}
          </div>
          <div class="font-small-3 text-muted">
            Realize
          </div>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ data.item.status }}
          </b-badge>
          <div
            v-if="data.item.status === 'RUNNING' && data.item.edge && !data.item.edge.isActive"
            class="font-small-2 text-warning mt-1"
            title="Bağlı edge pasif — yeni entry açılmaz, açık pozisyonlar yine yönetilir."
          >
            <feather-icon icon="PauseCircleIcon" /> Edge Pasif
          </div>
          <div
            v-if="data.item.lastError"
            class="font-small-2 text-danger mt-1"
            :title="data.item.lastError"
          >
            <feather-icon icon="AlertCircleIcon" /> {{ truncate(data.item.lastError, 60) }}
          </div>
        </template>
        <template #cell(actions)="data">
          <div class="text-right">
            <b-button
              v-if="data.item.status === 'RUNNING'"
              variant="outline-warning"
              size="sm"
              class="mr-1"
              @click="onStop(data.item.id)"
            >
              <feather-icon icon="StopCircleIcon" /> Durdur
            </b-button>
            <b-button
              v-else
              variant="outline-success"
              size="sm"
              class="mr-1"
              @click="onStart(data.item.id)"
            >
              <feather-icon icon="PlayCircleIcon" /> Başlat
            </b-button>
            <b-button
              variant="outline-info"
              size="sm"
              class="mr-1"
              @click="onShowDetail(data.item)"
            >
              <feather-icon icon="EyeIcon" /> Detay
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              class="mr-1"
              :disabled="data.item.status === 'RUNNING'"
              :title="data.item.status === 'RUNNING' ? 'Düzenlemek için önce durdurun' : 'Düzenle'"
              @click="openEditModal(data.item)"
            >
              <feather-icon icon="Edit2Icon" />
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              :disabled="data.item.status === 'RUNNING'"
              @click="onRemove(data.item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
        <template #table-busy>
          <div class="text-center py-2">
            <b-spinner small /> Yükleniyor...
          </div>
        </template>
      </b-table>
    </b-card>

    <!-- ─── Create Bot Modal ─────────────────────────────────────────────── -->
    <b-modal
      v-model="createModal.show"
      title="Yeni Bot Oluştur"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <b-row>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Bot Adı *</label>
          <b-form-input
            v-model="form.name"
            placeholder="BTC Pullback Testnet"
          />
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-form-checkbox
            v-model="editForm.inheritEdgeTpSl"
            switch
          >
            <strong>TP/SL değerlerini edge'den miras al</strong>
          </b-form-checkbox>
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-form-checkbox
            v-model="editForm.exitOnIndicatorSignal"
            switch
          >
            <strong>Exit sinyaliyle pozisyondan çık</strong>
          </b-form-checkbox>
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Mod *</label>
          <b-form-select
            v-model="form.mode"
            :options="[{value:'TESTNET',text:'Testnet (simüle)'},{value:'LIVE',text:'Live'}]"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Market *</label>
          <b-form-select
            v-model="form.market"
            :options="[{value:'FUTURES',text:'Futures'},{value:'SPOT',text:'Spot'}]"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">İndikatör *</label>
          <b-form-select
            v-model="form.indicatorId"
            :options="indicatorOptions"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Sembol *</label>
          <b-form-input
            v-model="form.symbol"
            placeholder="BTCUSDT"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Timeframe *</label>
          <b-form-select
            v-model="form.timeframe"
            :options="tfOptions"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">TP % *</label>
          <b-form-input
            v-model.number="form.tpPerc"
            type="number"
            step="0.1"
            min="0.1"
            :disabled="editForm.inheritEdgeTpSl"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">SL % *</label>
          <b-form-input
            v-model.number="form.slPerc"
            type="number"
            step="0.1"
            min="0.1"
            :disabled="editForm.inheritEdgeTpSl"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Kaldıraç *</label>
          <b-form-input
            v-model.number="form.leverage"
            type="number"
            step="1"
            min="1"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Komisyon/Yön %</label>
          <b-form-input
            v-model.number="form.commissionPerSide"
            type="number"
            step="0.01"
            min="0"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">
            Sermaye (USDT)
            <span
              v-if="form.mode === 'LIVE'"
              class="text-danger"
            >*</span>
            <span v-else>— testnet için isteğe bağlı</span>
          </label>
          <b-form-input
            v-model.number="form.capitalAllocated"
            type="number"
            step="10"
            min="0"
          />
          <small
            v-if="form.mode === 'LIVE'"
            class="text-warning"
          >
            Bota verilecek sermaye miktarı (borsadaki gerçek bakiyenden bağımsız — qty hesabında kullanılır).
            Hedef'le birlikte çalışırken hedef bu miktarı bot'a tahsis eder.
          </small>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Hedef (opsiyonel)</label>
          <b-form-select
            v-model="form.targetId"
            :options="targetOptions"
          />
          <small class="text-muted">Bot bir hedefe bağlanırsa PnL'i o hedefe yazılır.</small>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">
            API Anahtarı
            <span
              v-if="form.mode === 'LIVE'"
              class="text-danger"
            >*</span>
            <span v-else>(LIVE için zorunlu)</span>
          </label>
          <b-form-select
            v-model="form.apiKeyId"
            :options="apiKeyOptions"
          />
        </b-col>
      </b-row>

      <!-- Gelişmiş Stop ayarları (trail / step) -->
      <div class="mt-2">
        <b-button
          v-b-toggle.create-advanced-stops
          variant="flat-secondary"
          size="sm"
          block
          class="text-left"
        >
          <feather-icon icon="SlidersIcon" /> Gelişmiş Stop Ayarları (trail / kademeli)
        </b-button>
        <b-collapse
          id="create-advanced-stops"
          class="mt-1"
        >
          <b-card
            no-body
            class="border bg-light p-1"
          >
            <b-row>
              <b-col
                cols="12"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="form.trailStopEnabled"
                  switch
                >
                  <strong>Trail Stop</strong> — kar belli seviyeyi geçince SL fiyatı takip etsin
                </b-form-checkbox>
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Trigger profit %</label>
                <b-form-input
                  v-model.number="form.trail.triggerProfit"
                  type="number"
                  step="0.1"
                  min="0"
                  :disabled="!form.trailStopEnabled"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Trail % (peak'in altında)</label>
                <b-form-input
                  v-model.number="form.trail.trailPercent"
                  type="number"
                  step="0.1"
                  min="0"
                  :disabled="!form.trailStopEnabled"
                />
              </b-col>
            </b-row>
            <hr class="my-1">
            <b-row>
              <b-col
                cols="12"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="form.stepStopEnabled"
                  switch
                >
                  <strong>Kademeli (Step) Stop</strong> — kar eşiklerinde SL'i belirli yerlere taşı
                </b-form-checkbox>
              </b-col>
              <b-col
                cols="12"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Steps JSON ({{ '[{ atProfit, moveStopTo }]' }})</label>
                <b-form-textarea
                  v-model="form.stepStopJson"
                  rows="3"
                  :disabled="!form.stepStopEnabled"
                  placeholder="[{&quot;atProfit&quot;:1,&quot;moveStopTo&quot;:0},{&quot;atProfit&quot;:2,&quot;moveStopTo&quot;:1}]"
                />
                <small
                  v-if="stepJsonError"
                  class="text-danger"
                >{{ stepJsonError }}</small>
                <small
                  v-else
                  class="text-muted"
                >atProfit: % kar eşiği, moveStopTo: entry'den % offset (LONG için pozitif = kar kilidi)</small>
              </b-col>
            </b-row>
          </b-card>
        </b-collapse>
      </div>

      <!-- Pozisyon / KZ yönetimi -->
      <div class="mt-2">
        <b-button
          v-b-toggle.create-position-management
          variant="flat-secondary"
          size="sm"
          block
          class="text-left"
        >
          <feather-icon icon="ShieldIcon" /> Pozisyon / KZ Yönetimi
        </b-button>
        <b-collapse
          id="create-position-management"
          class="mt-1"
        >
          <b-card
            no-body
            class="border bg-light p-1"
          >
            <b-row>
              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="form.positionManagement.breakEven.enabled"
                  switch
                >
                  <strong>Fee-aware break-even</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Trigger %</label>
                    <b-form-input
                      v-model.number="form.positionManagement.breakEven.triggerProfit"
                      type="number"
                      step="0.05"
                      min="0"
                      :disabled="!form.positionManagement.breakEven.enabled"
                    />
                  </b-col>
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Buffer %</label>
                    <b-form-input
                      v-model.number="form.positionManagement.breakEven.bufferProfit"
                      type="number"
                      step="0.01"
                      min="0"
                      :disabled="!form.positionManagement.breakEven.enabled"
                    />
                  </b-col>
                  <b-col
                    cols="12"
                    class="mt-50"
                  >
                    <b-form-checkbox
                      v-model="form.positionManagement.breakEven.includeFees"
                      :disabled="!form.positionManagement.breakEven.enabled"
                    >
                      Komisyonu dahil et
                    </b-form-checkbox>
                  </b-col>
                  <b-col
                    cols="6"
                    class="mt-50"
                  >
                    <label class="font-small-3 text-muted">Buffer modu</label>
                    <b-form-select
                      v-model="form.positionManagement.breakEven.bufferMode"
                      :options="breakEvenBufferModeOptions"
                      :disabled="!form.positionManagement.breakEven.enabled"
                    />
                  </b-col>
                  <b-col
                    cols="6"
                    class="mt-50"
                  >
                    <label class="font-small-3 text-muted">Kayma tamponu %</label>
                    <b-form-input
                      v-model.number="form.positionManagement.breakEven.slippageBufferPct"
                      type="number"
                      step="0.01"
                      min="0"
                      :disabled="!form.positionManagement.breakEven.enabled || form.positionManagement.breakEven.bufferMode === 'RAW'"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="form.positionManagement.partialTp.enabled"
                  switch
                >
                  <strong>Partial TP</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Kâr %</label>
                    <b-form-input
                      v-model.number="form.positionManagement.partialTp.atProfit"
                      type="number"
                      step="0.05"
                      min="0.01"
                      :disabled="!form.positionManagement.partialTp.enabled"
                    />
                  </b-col>
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Kapat %</label>
                    <b-form-input
                      v-model.number="form.positionManagement.partialTp.takePercent"
                      type="number"
                      step="5"
                      min="1"
                      max="95"
                      :disabled="!form.positionManagement.partialTp.enabled"
                    />
                  </b-col>
                  <b-col
                    cols="12"
                    class="mt-50"
                  >
                    <b-form-checkbox
                      v-model="form.positionManagement.partialTp.moveRemainderToBreakEven"
                      :disabled="!form.positionManagement.partialTp.enabled"
                    >
                      Kalanı break-even'a taşı
                    </b-form-checkbox>
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="form.positionManagement.trailTp.enabled"
                  switch
                >
                  <strong>Trail TP / profit lock</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Trigger %</label>
                    <b-form-input
                      v-model.number="form.positionManagement.trailTp.triggerProfit"
                      type="number"
                      step="0.05"
                      min="0"
                      :disabled="!form.positionManagement.trailTp.enabled"
                    />
                  </b-col>
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Aralık %</label>
                    <b-form-input
                      v-model.number="form.positionManagement.trailTp.trailPercent"
                      type="number"
                      step="0.05"
                      min="0.01"
                      :disabled="!form.positionManagement.trailTp.enabled"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="form.positionManagement.riskSizing.enabled"
                  switch
                >
                  <strong>Risk bazlı qty</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Risk USDT</label>
                    <b-form-input
                      v-model.number="form.positionManagement.riskSizing.riskUsdtPerTrade"
                      type="number"
                      step="5"
                      min="0"
                      :disabled="!form.positionManagement.riskSizing.enabled"
                    />
                  </b-col>
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Max sermaye %</label>
                    <b-form-input
                      v-model.number="form.positionManagement.riskSizing.maxCapitalUsagePercent"
                      type="number"
                      step="5"
                      min="1"
                      max="100"
                      :disabled="!form.positionManagement.riskSizing.enabled"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="form.positionManagement.dailyGuard.enabled"
                  switch
                >
                  <strong>Günlük / seri zarar freni</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col cols="4">
                    <label class="font-small-3 text-muted">Günlük max zarar</label>
                    <b-form-input
                      v-model.number="form.positionManagement.dailyGuard.maxDailyLossUsdt"
                      type="number"
                      step="10"
                      min="0"
                      :disabled="!form.positionManagement.dailyGuard.enabled"
                    />
                  </b-col>
                  <b-col cols="4">
                    <label class="font-small-3 text-muted">Seri zarar</label>
                    <b-form-input
                      v-model.number="form.positionManagement.dailyGuard.maxConsecutiveLosses"
                      type="number"
                      step="1"
                      min="1"
                      :disabled="!form.positionManagement.dailyGuard.enabled"
                    />
                  </b-col>
                  <b-col cols="4">
                    <label class="font-small-3 text-muted">Cooldown dk</label>
                    <b-form-input
                      v-model.number="form.positionManagement.dailyGuard.cooldownMinutes"
                      type="number"
                      step="5"
                      min="0"
                      :disabled="!form.positionManagement.dailyGuard.enabled"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="form.positionManagement.dailyProfitGuard.enabled"
                  switch
                >
                  <strong>Günlük TP limiti</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <label class="font-small-3 text-muted">Günlük max kar</label>
                    <b-form-input
                      v-model.number="form.positionManagement.dailyProfitGuard.maxDailyProfitUsdt"
                      type="number"
                      step="10"
                      min="1"
                      :disabled="!form.positionManagement.dailyProfitGuard.enabled"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="form.positionManagement.globalExposure.enabled"
                  switch
                >
                  <strong>Aynı coin tekrar giriş kilidi</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-checkbox
                      v-model="form.positionManagement.globalExposure.blockOppositeSide"
                      :disabled="!form.positionManagement.globalExposure.enabled"
                    >
                      Ters yönü de engelle
                    </b-form-checkbox>
                  </b-col>
                  <b-col
                    cols="12"
                    md="6"
                  >
                    <label class="font-small-3 text-muted">Kapsam</label>
                    <b-form-select
                      v-model="form.positionManagement.globalExposure.scope"
                      :options="[{value:'GLOBAL',text:'Tüm hedefler/botlar'}, {value:'TARGET',text:'Sadece aynı hedef'}]"
                      :disabled="!form.positionManagement.globalExposure.enabled"
                    />
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-card>
        </b-collapse>
      </div>

      <div class="text-right mt-2">
        <b-button
          variant="secondary"
          class="mr-1"
          @click="createModal.show = false"
        >
          İptal
        </b-button>
        <b-button
          variant="primary"
          :disabled="!canSubmit || !!stepJsonError"
          @click="onSubmit"
        >
          <feather-icon icon="CheckIcon" /> Oluştur
        </b-button>
      </div>
    </b-modal>

    <!-- ─── Edit Bot Modal ────────────────────────────────────────────────── -->
    <b-modal
      v-model="editModal.show"
      :title="`Botu Düzenle — ${editModal.original ? editModal.original.name : ''}`"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <b-alert
        show
        variant="info"
        class="mb-2 py-1 px-1"
      >
        <small>
          <feather-icon icon="InfoIcon" /> Mod / Market / İndikatör değiştirilemez. Bunlar için yeni bir bot oluşturun.
        </small>
      </b-alert>
      <b-row>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Bot Adı *</label>
          <b-form-input
            v-model="editForm.name"
            placeholder="BTC Pullback Testnet"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Sembol</label>
          <b-form-input
            v-model="editForm.symbol"
            placeholder="BTCUSDT"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Timeframe</label>
          <b-form-select
            v-model="editForm.timeframe"
            :options="tfOptions"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">TP % *</label>
          <b-form-input
            v-model.number="editForm.tpPerc"
            type="number"
            step="0.1"
            min="0.1"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">SL % *</label>
          <b-form-input
            v-model.number="editForm.slPerc"
            type="number"
            step="0.1"
            min="0.1"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Kaldıraç *</label>
          <b-form-input
            v-model.number="editForm.leverage"
            type="number"
            step="1"
            min="1"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Komisyon/Yön %</label>
          <b-form-input
            v-model.number="editForm.commissionPerSide"
            type="number"
            step="0.01"
            min="0"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">
            Sermaye (USDT)
            <span
              v-if="editModal.original && editModal.original.mode === 'LIVE'"
              class="text-danger"
            >*</span>
          </label>
          <b-form-input
            v-model.number="editForm.capitalAllocated"
            type="number"
            step="10"
            min="0"
          />
          <small
            v-if="editModal.original && editModal.original.mode === 'LIVE'"
            class="text-warning"
          >
            Bota ayrılan lojik sermaye — qty hesabında kullanılır.
          </small>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Hedef (opsiyonel)</label>
          <b-form-select
            v-model="editForm.targetId"
            :options="targetOptions"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">API Anahtarı (LIVE için zorunlu)</label>
          <b-form-select
            v-model="editForm.apiKeyId"
            :options="apiKeyOptions"
          />
        </b-col>
      </b-row>

      <!-- Gelişmiş Stop ayarları (trail / step) -->
      <div class="mt-2">
        <b-button
          v-b-toggle.edit-advanced-stops
          variant="flat-secondary"
          size="sm"
          block
          class="text-left"
        >
          <feather-icon icon="SlidersIcon" /> Gelişmiş Stop Ayarları (trail / kademeli)
        </b-button>
        <b-collapse
          id="edit-advanced-stops"
          class="mt-1"
          :visible="editForm.trailStopEnabled || editForm.stepStopEnabled"
        >
          <b-card
            no-body
            class="border bg-light p-1"
          >
            <b-row>
              <b-col
                cols="12"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="editForm.trailStopEnabled"
                  switch
                >
                  <strong>Trail Stop</strong>
                </b-form-checkbox>
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Trigger profit %</label>
                <b-form-input
                  v-model.number="editForm.trail.triggerProfit"
                  type="number"
                  step="0.1"
                  min="0"
                  :disabled="!editForm.trailStopEnabled"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Trail %</label>
                <b-form-input
                  v-model.number="editForm.trail.trailPercent"
                  type="number"
                  step="0.1"
                  min="0"
                  :disabled="!editForm.trailStopEnabled"
                />
              </b-col>
            </b-row>
            <hr class="my-1">
            <b-row>
              <b-col
                cols="12"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="editForm.stepStopEnabled"
                  switch
                >
                  <strong>Kademeli Stop</strong>
                </b-form-checkbox>
              </b-col>
              <b-col
                cols="12"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Steps JSON</label>
                <b-form-textarea
                  v-model="editForm.stepStopJson"
                  rows="3"
                  :disabled="!editForm.stepStopEnabled"
                  placeholder="[{&quot;atProfit&quot;:1,&quot;moveStopTo&quot;:0},{&quot;atProfit&quot;:2,&quot;moveStopTo&quot;:1}]"
                />
                <small
                  v-if="editStepJsonError"
                  class="text-danger"
                >{{ editStepJsonError }}</small>
              </b-col>
            </b-row>
          </b-card>
        </b-collapse>
      </div>

      <div class="mt-2">
        <b-button
          v-b-toggle.edit-position-management
          variant="flat-secondary"
          size="sm"
          block
          class="text-left"
        >
          <feather-icon icon="ShieldIcon" /> Pozisyon / KZ Yönetimi
        </b-button>
        <b-collapse
          id="edit-position-management"
          class="mt-1"
          :visible="hasPositionManagementEnabled(editForm.positionManagement)"
        >
          <b-card
            no-body
            class="border bg-light p-1"
          >
            <b-row>
              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="editForm.positionManagement.breakEven.enabled"
                  switch
                >
                  <strong>Fee-aware break-even</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Trigger %</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.breakEven.triggerProfit"
                      type="number"
                      step="0.05"
                      min="0"
                      :disabled="!editForm.positionManagement.breakEven.enabled"
                    />
                  </b-col>
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Buffer %</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.breakEven.bufferProfit"
                      type="number"
                      step="0.01"
                      min="0"
                      :disabled="!editForm.positionManagement.breakEven.enabled"
                    />
                  </b-col>
                  <b-col
                    cols="12"
                    class="mt-50"
                  >
                    <b-form-checkbox
                      v-model="editForm.positionManagement.breakEven.includeFees"
                      :disabled="!editForm.positionManagement.breakEven.enabled"
                    >
                      Komisyonu dahil et
                    </b-form-checkbox>
                  </b-col>
                  <b-col
                    cols="6"
                    class="mt-50"
                  >
                    <label class="font-small-3 text-muted">Buffer modu</label>
                    <b-form-select
                      v-model="editForm.positionManagement.breakEven.bufferMode"
                      :options="breakEvenBufferModeOptions"
                      :disabled="!editForm.positionManagement.breakEven.enabled"
                    />
                  </b-col>
                  <b-col
                    cols="6"
                    class="mt-50"
                  >
                    <label class="font-small-3 text-muted">Kayma tamponu %</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.breakEven.slippageBufferPct"
                      type="number"
                      step="0.01"
                      min="0"
                      :disabled="!editForm.positionManagement.breakEven.enabled || editForm.positionManagement.breakEven.bufferMode === 'RAW'"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="editForm.positionManagement.partialTp.enabled"
                  switch
                >
                  <strong>Partial TP</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Kâr %</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.partialTp.atProfit"
                      type="number"
                      step="0.05"
                      min="0.01"
                      :disabled="!editForm.positionManagement.partialTp.enabled"
                    />
                  </b-col>
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Kapat %</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.partialTp.takePercent"
                      type="number"
                      step="5"
                      min="1"
                      max="95"
                      :disabled="!editForm.positionManagement.partialTp.enabled"
                    />
                  </b-col>
                  <b-col
                    cols="12"
                    class="mt-50"
                  >
                    <b-form-checkbox
                      v-model="editForm.positionManagement.partialTp.moveRemainderToBreakEven"
                      :disabled="!editForm.positionManagement.partialTp.enabled"
                    >
                      Kalanı break-even'a taşı
                    </b-form-checkbox>
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="editForm.positionManagement.trailTp.enabled"
                  switch
                >
                  <strong>Trail TP / profit lock</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Trigger %</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.trailTp.triggerProfit"
                      type="number"
                      step="0.05"
                      min="0"
                      :disabled="!editForm.positionManagement.trailTp.enabled"
                    />
                  </b-col>
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Aralık %</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.trailTp.trailPercent"
                      type="number"
                      step="0.05"
                      min="0.01"
                      :disabled="!editForm.positionManagement.trailTp.enabled"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="editForm.positionManagement.riskSizing.enabled"
                  switch
                >
                  <strong>Risk bazlı qty</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Risk USDT</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.riskSizing.riskUsdtPerTrade"
                      type="number"
                      step="5"
                      min="0"
                      :disabled="!editForm.positionManagement.riskSizing.enabled"
                    />
                  </b-col>
                  <b-col cols="6">
                    <label class="font-small-3 text-muted">Max sermaye %</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.riskSizing.maxCapitalUsagePercent"
                      type="number"
                      step="5"
                      min="1"
                      max="100"
                      :disabled="!editForm.positionManagement.riskSizing.enabled"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="editForm.positionManagement.dailyGuard.enabled"
                  switch
                >
                  <strong>Günlük / seri zarar freni</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col cols="4">
                    <label class="font-small-3 text-muted">Günlük max zarar</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.dailyGuard.maxDailyLossUsdt"
                      type="number"
                      step="10"
                      min="0"
                      :disabled="!editForm.positionManagement.dailyGuard.enabled"
                    />
                  </b-col>
                  <b-col cols="4">
                    <label class="font-small-3 text-muted">Seri zarar</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.dailyGuard.maxConsecutiveLosses"
                      type="number"
                      step="1"
                      min="1"
                      :disabled="!editForm.positionManagement.dailyGuard.enabled"
                    />
                  </b-col>
                  <b-col cols="4">
                    <label class="font-small-3 text-muted">Cooldown dk</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.dailyGuard.cooldownMinutes"
                      type="number"
                      step="5"
                      min="0"
                      :disabled="!editForm.positionManagement.dailyGuard.enabled"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="editForm.positionManagement.dailyProfitGuard.enabled"
                  switch
                >
                  <strong>Günlük TP limiti</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <label class="font-small-3 text-muted">Günlük max kar</label>
                    <b-form-input
                      v-model.number="editForm.positionManagement.dailyProfitGuard.maxDailyProfitUsdt"
                      type="number"
                      step="10"
                      min="1"
                      :disabled="!editForm.positionManagement.dailyProfitGuard.enabled"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                cols="12"
                class="mb-1"
              >
                <b-form-checkbox
                  v-model="editForm.positionManagement.globalExposure.enabled"
                  switch
                >
                  <strong>Aynı coin tekrar giriş kilidi</strong>
                </b-form-checkbox>
                <b-row class="mt-1">
                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-checkbox
                      v-model="editForm.positionManagement.globalExposure.blockOppositeSide"
                      :disabled="!editForm.positionManagement.globalExposure.enabled"
                    >
                      Ters yönü de engelle
                    </b-form-checkbox>
                  </b-col>
                  <b-col
                    cols="12"
                    md="6"
                  >
                    <label class="font-small-3 text-muted">Kapsam</label>
                    <b-form-select
                      v-model="editForm.positionManagement.globalExposure.scope"
                      :options="[{value:'GLOBAL',text:'Tüm hedefler/botlar'}, {value:'TARGET',text:'Sadece aynı hedef'}]"
                      :disabled="!editForm.positionManagement.globalExposure.enabled"
                    />
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-card>
        </b-collapse>
      </div>

      <div class="text-right mt-2">
        <b-button
          variant="secondary"
          class="mr-1"
          @click="editModal.show = false"
        >
          İptal
        </b-button>
        <b-button
          variant="primary"
          :disabled="!canSubmitEdit || !!editStepJsonError"
          @click="onUpdate"
        >
          <feather-icon icon="CheckIcon" /> Kaydet
        </b-button>
      </div>
    </b-modal>

    <!-- ─── Edit Position Stops Modal ─────────────────────────────────────── -->
    <b-modal
      v-model="stopsModal.show"
      title="Pozisyon TP/SL Düzenle"
      size="md"
      hide-footer
      no-close-on-backdrop
    >
      <div v-if="stopsModal.position">
        <p class="mb-1 font-small-3 text-muted">
          <b-badge :variant="stopsModal.position.side === 'LONG' ? 'success' : 'danger'">
            {{ stopsModal.position.side }}
          </b-badge>
          {{ stopsModal.position.symbol }} · Giriş: <strong>{{ formatPrice(stopsModal.position.entryPrice) }}</strong>
        </p>
        <b-row>
          <b-col
            cols="6"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">TP Fiyatı</label>
            <b-form-input
              v-model.number="stopsForm.tpPrice"
              type="number"
              step="0.0001"
              min="0"
            />
          </b-col>
          <b-col
            cols="6"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">SL Fiyatı</label>
            <b-form-input
              v-model.number="stopsForm.slPrice"
              type="number"
              step="0.0001"
              min="0"
            />
          </b-col>
        </b-row>
        <small class="text-muted">
          Sadece girdiğin alanlar güncellenir. Boş bırakırsan o değer aynı kalır.
        </small>
      </div>
      <div class="text-right mt-2">
        <b-button
          variant="secondary"
          class="mr-1"
          @click="stopsModal.show = false"
        >
          İptal
        </b-button>
        <b-button
          variant="primary"
          :disabled="!canSubmitStops"
          @click="onSubmitStops"
        >
          <feather-icon icon="CheckIcon" /> Kaydet
        </b-button>
      </div>
    </b-modal>

    <!-- ─── Detail Modal ──────────────────────────────────────────────────── -->
    <b-modal
      v-model="detailModal.show"
      :title="`Bot Detay — ${detailModal.item ? detailModal.item.name : ''}`"
      size="xl"
      hide-footer
      no-close-on-backdrop
    >
      <div v-if="detailModal.item">
        <b-card
          no-body
          class="mb-2 bg-light-info"
        >
          <b-card-body class="py-1">
            <b-row class="text-center">
              <b-col
                cols="6"
                md="3"
              >
                <small class="text-muted">Durum</small>
                <h5><b-badge :variant="statusVariant(detailModal.item.status)">
                  {{ detailModal.item.status }}
                </b-badge></h5>
              </b-col>
              <b-col
                cols="6"
                md="3"
              >
                <small class="text-muted">Realize PnL</small>
                <h5 :class="(Number(detailModal.item.realizedPnl) || 0) >= 0 ? 'text-success' : 'text-danger'">
                  {{ formatNum(detailModal.item.realizedPnl, 4) }}
                </h5>
              </b-col>
              <b-col
                cols="6"
                md="3"
              >
                <small class="text-muted">Açık Pozisyon</small>
                <h5>{{ openPositionsCount }}</h5>
              </b-col>
              <b-col
                cols="6"
                md="3"
              >
                <small class="text-muted">Toplam Trade</small>
                <h5>{{ trades.length }}</h5>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <b-tabs>
          <b-tab
            title="Açık Pozisyonlar"
            active
          >
            <b-table
              :items="openPositions"
              :fields="positionFields"
              small
              striped
              responsive
              show-empty
              empty-text="Açık pozisyon yok."
            >
              <template #cell(side)="data">
                <b-badge :variant="data.item.side === 'LONG' ? 'success' : 'danger'">
                  {{ data.item.side }}
                </b-badge>
              </template>
              <template #cell(entryPrice)="data">
                {{ formatPrice(data.item.entryPrice) }}
              </template>
              <template #cell(tpPrice)="data">
                {{ formatPrice(data.item.tpPrice) }}
              </template>
              <template #cell(slPrice)="data">
                {{ formatPrice(data.item.slPrice) }}
              </template>
              <template #cell(protectionWarning)="data">
                <b-badge
                  v-if="protectiveWarningText(data.item)"
                  variant="warning"
                  :title="protectiveWarningText(data.item)"
                >
                  Uyarı
                </b-badge>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>
              <template #cell(openedAt)="data">
                {{ formatDateTime(data.item.openedAt) }}
              </template>
              <template #cell(positionActions)="data">
                <b-button-group size="sm">
                  <b-button
                    variant="outline-primary"
                    title="TP/SL Düzenle"
                    @click="openStopsModal(data.item)"
                  >
                    <feather-icon icon="Edit2Icon" />
                  </b-button>
                  <b-button
                    variant="outline-warning"
                    title="Manuel Kapat"
                    @click="onClosePosition(data.item)"
                  >
                    <feather-icon icon="XCircleIcon" /> Kapat
                  </b-button>
                </b-button-group>
              </template>
            </b-table>
          </b-tab>
          <b-tab title="Trade Geçmişi">
            <b-table
              :items="trades"
              :fields="tradeFields"
              small
              striped
              responsive
              show-empty
              empty-text="Henüz trade yok."
            >
              <template #cell(action)="data">
                <b-badge :variant="data.item.action === 'ENTRY' ? 'info' : 'warning'">
                  {{ data.item.action }}
                </b-badge>
              </template>
              <template #cell(side)="data">
                <b-badge :variant="data.item.side === 'LONG' ? 'success' : 'danger'">
                  {{ data.item.side }}
                </b-badge>
              </template>
              <template #cell(price)="data">
                {{ formatPrice(data.item.price) }}
              </template>
              <template #cell(pnl)="data">
                <span
                  v-if="data.item.pnl !== null"
                  :class="Number(data.item.pnl) >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ formatNum(data.item.pnl, 4) }}
                  <small v-if="data.item.pnlPercent !== null">({{ formatNum(data.item.pnlPercent, 2) }}%)</small>
                </span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>
              <template #cell(occurredAt)="data">
                {{ formatDateTime(data.item.occurredAt) }}
              </template>
              <template #cell(reason)="data">
                <div
                  class="font-small-3 text-muted"
                  style="max-width:250px;"
                >
                  {{ data.item.reason || '—' }}
                </div>
              </template>
            </b-table>
          </b-tab>
          <b-tab title="Cüzdan (LIVE)">
            <div
              v-if="!wallets.length"
              class="text-muted text-center py-2"
            >
              Henüz cüzdan snapshot'ı yok. Sadece LIVE bot'lar için start/stop'ta otomatik alınır.
            </div>
            <b-table
              v-else
              :items="wallets"
              :fields="walletFields"
              small
              striped
              responsive
            >
              <template #cell(phase)="data">
                <b-badge :variant="(data.item.meta && data.item.meta.phase) === 'start' ? 'info' : (data.item.meta && data.item.meta.phase) === 'stop' ? 'warning' : 'secondary'">
                  {{ (data.item.meta && data.item.meta.phase) || '—' }}
                </b-badge>
              </template>
              <template #cell(balance)="data">
                {{ formatNum(data.item.balance, 6) }}
              </template>
              <template #cell(available)="data">
                {{ formatNum(data.item.available, 6) }}
              </template>
              <template #cell(inOrder)="data">
                <span v-if="data.item.inOrder !== null">{{ formatNum(data.item.inOrder, 6) }}</span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>
              <template #cell(unrealizedPnl)="data">
                <span
                  v-if="data.item.unrealizedPnl !== null"
                  :class="Number(data.item.unrealizedPnl) >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ formatNum(data.item.unrealizedPnl, 6) }}
                </span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>
              <template #cell(snapshotAt)="data">
                {{ formatDateTime(data.item.snapshotAt) }}
              </template>
            </b-table>
          </b-tab>
          <b-tab title="Tüm Pozisyonlar">
            <b-table
              :items="allPositions"
              :fields="allPositionFields"
              small
              striped
              responsive
              show-empty
              empty-text="Pozisyon yok."
            >
              <template #cell(side)="data">
                <b-badge :variant="data.item.side === 'LONG' ? 'success' : 'danger'">
                  {{ data.item.side }}
                </b-badge>
              </template>
              <template #cell(status)="data">
                <b-badge :variant="data.item.status === 'OPEN' ? 'warning' : 'secondary'">
                  {{ data.item.status }}
                </b-badge>
              </template>
              <template #cell(entryPrice)="data">
                {{ formatPrice(data.item.entryPrice) }}
              </template>
              <template #cell(exitPrice)="data">
                <span v-if="data.item.exitPrice">{{ formatPrice(data.item.exitPrice) }}</span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>
              <template #cell(realizedPnl)="data">
                <span
                  v-if="data.item.realizedPnl !== null"
                  :class="Number(data.item.realizedPnl) >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ formatNum(data.item.realizedPnl, 4) }}
                  <small v-if="data.item.pnlPercent !== null">({{ formatNum(data.item.pnlPercent, 2) }}%)</small>
                </span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>
              <template #cell(protectionWarning)="data">
                <b-badge
                  v-if="protectiveWarningText(data.item)"
                  variant="warning"
                  :title="protectiveWarningText(data.item)"
                >
                  Uyarı
                </b-badge>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>
              <template #cell(openedAt)="data">
                {{ formatDateTime(data.item.openedAt) }}
              </template>
              <template #cell(closedAt)="data">
                <span v-if="data.item.closedAt">{{ formatDateTime(data.item.closedAt) }}</span>
                <span
                  v-else
                  class="text-warning"
                >Açık</span>
              </template>
            </b-table>
          </b-tab>
        </b-tabs>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BTable, BButton, BButtonGroup, BBadge, BSpinner,
  BFormInput, BFormSelect, BFormCheckbox, BFormTextarea,
  BRow, BCol, BModal, BTabs, BTab, BAlert, BCollapse, VBToggle,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getSocket } from '@/libs/socket'

export default {
  name: 'BotsIndex',
  components: {
    BCard,
    BCardBody,
    BTable,
    BButton,
    BButtonGroup,
    BBadge,
    BSpinner,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BFormTextarea,
    BRow,
    BCol,
    BModal,
    BTabs,
    BTab,
    BAlert,
    BCollapse,
  },
  directives: { 'b-toggle': VBToggle },
  data() {
    return {
      loading: false,
      searchTimer: null,
      filter: {
        status: undefined, mode: undefined, market: undefined, search: '',
      },
      statusOptions: [
        { value: undefined, text: 'Tümü' },
        { value: 'IDLE', text: 'Idle' },
        { value: 'RUNNING', text: 'Çalışıyor' },
        { value: 'PAUSED', text: 'Duraklatıldı' },
        { value: 'STOPPED', text: 'Durduruldu' },
        { value: 'ERROR', text: 'Hata' },
      ],
      modeOptions: [
        { value: undefined, text: 'Tümü' },
        { value: 'TESTNET', text: 'Testnet' },
        { value: 'LIVE', text: 'Live' },
      ],
      marketOptions: [
        { value: undefined, text: 'Tümü' },
        { value: 'FUTURES', text: 'Futures' },
        { value: 'SPOT', text: 'Spot' },
      ],
      tfOptions: ['1m', '3m', '5m', '15m', '30m', '1h', '4h'].map(v => ({ value: v, text: v })),
      breakEvenBufferModeOptions: [
        { value: 'NET', text: 'Net hedef' },
        { value: 'RAW', text: 'Ham fiyat' },
      ],
      fields: [
        { key: 'id', label: '#', thClass: 'width-50' },
        { key: 'name', label: 'Bot' },
        { key: 'indicator', label: 'İndikatör' },
        { key: 'tpsl', label: 'TP/SL' },
        { key: 'pnl', label: 'PnL', thClass: 'width-120' },
        { key: 'status', label: 'Durum', thClass: 'width-120 text-center' },
        { key: 'actions', label: '', thClass: 'width-280 text-nowrap' },
      ],
      positionFields: [
        { key: 'id', label: '#' },
        { key: 'symbol', label: 'Sembol' },
        { key: 'side', label: 'Yön' },
        { key: 'entryPrice', label: 'Giriş Fiyat' },
        { key: 'tpPrice', label: 'TP Fiyat' },
        { key: 'slPrice', label: 'SL Fiyat' },
        { key: 'qty', label: 'Miktar' },
        { key: 'protectionWarning', label: 'Koruma' },
        { key: 'openedAt', label: 'Açılış' },
        { key: 'positionActions', label: '', thClass: 'text-nowrap' },
      ],
      tradeFields: [
        { key: 'id', label: '#' },
        { key: 'symbol', label: 'Sembol' },
        { key: 'side', label: 'Yön' },
        { key: 'action', label: 'Aksiyon' },
        { key: 'price', label: 'Fiyat' },
        { key: 'pnl', label: 'PnL' },
        { key: 'occurredAt', label: 'Zaman' },
        { key: 'reason', label: 'Sebep' },
      ],
      walletFields: [
        { key: 'snapshotAt', label: 'Zaman' },
        { key: 'phase', label: 'Aşama' },
        { key: 'asset', label: 'Asset' },
        { key: 'balance', label: 'Toplam' },
        { key: 'available', label: 'Boş' },
        { key: 'inOrder', label: 'Emirde' },
        { key: 'unrealizedPnl', label: 'Unrealized' },
      ],
      allPositionFields: [
        { key: 'id', label: '#' },
        { key: 'symbol', label: 'Sembol' },
        { key: 'side', label: 'Yön' },
        { key: 'status', label: 'Durum' },
        { key: 'entryPrice', label: 'Giriş' },
        { key: 'exitPrice', label: 'Çıkış' },
        { key: 'realizedPnl', label: 'PnL' },
        { key: 'protectionWarning', label: 'Koruma' },
        { key: 'openedAt', label: 'Açılış' },
        { key: 'closedAt', label: 'Kapanış' },
      ],
      createModal: { show: false },
      form: {
        name: '',
        mode: 'TESTNET',
        market: 'FUTURES',
        indicatorId: null,
        targetId: null,
        apiKeyId: null,
        symbol: 'BTCUSDT',
        timeframe: '5m',
        tpPerc: 0.5,
        slPerc: 0.3,
        leverage: 10,
        commissionPerSide: 0.05,
        capitalAllocated: 1000,
        trailStopEnabled: false,
        trail: { triggerProfit: 0.5, trailPercent: 0.3 },
        stepStopEnabled: false,
        stepStopJson: '',
        positionManagement: {
          breakEven: {
            enabled: false, triggerProfit: 0.35, bufferProfit: 0.05, includeFees: true, bufferMode: 'NET', slippageBufferPct: 0.05,
          },
          partialTp: {
            enabled: false, atProfit: 0.5, takePercent: 40, moveRemainderToBreakEven: true,
          },
          trailTp: { enabled: false, triggerProfit: 0.5, trailPercent: 0.35 },
          riskSizing: { enabled: false, riskUsdtPerTrade: 50, maxCapitalUsagePercent: 100 },
          dailyGuard: {
            enabled: false, maxDailyLossUsdt: 150, maxConsecutiveLosses: 2, cooldownMinutes: 60,
          },
          dailyProfitGuard: { enabled: false, maxDailyProfitUsdt: 100 },
          globalExposure: { enabled: true, blockOppositeSide: true, scope: 'GLOBAL' },
        },
        inheritEdgeTpSl: true,
        exitOnIndicatorSignal: true,
      },
      editModal: { show: false, original: null },
      editForm: {
        name: '',
        symbol: '',
        timeframe: '5m',
        tpPerc: 0.5,
        slPerc: 0.3,
        leverage: 10,
        commissionPerSide: 0.05,
        capitalAllocated: 0,
        targetId: null,
        apiKeyId: null,
        trailStopEnabled: false,
        trail: { triggerProfit: 0.5, trailPercent: 0.3 },
        stepStopEnabled: false,
        stepStopJson: '',
        positionManagement: {
          breakEven: {
            enabled: false, triggerProfit: 0.35, bufferProfit: 0.05, includeFees: true, bufferMode: 'NET', slippageBufferPct: 0.05,
          },
          partialTp: {
            enabled: false, atProfit: 0.5, takePercent: 40, moveRemainderToBreakEven: true,
          },
          trailTp: { enabled: false, triggerProfit: 0.5, trailPercent: 0.35 },
          riskSizing: { enabled: false, riskUsdtPerTrade: 50, maxCapitalUsagePercent: 100 },
          dailyGuard: {
            enabled: false, maxDailyLossUsdt: 150, maxConsecutiveLosses: 2, cooldownMinutes: 60,
          },
          dailyProfitGuard: { enabled: false, maxDailyProfitUsdt: 100 },
          globalExposure: { enabled: true, blockOppositeSide: true, scope: 'GLOBAL' },
        },
      },
      stopsModal: { show: false, position: null },
      stopsForm: { tpPrice: null, slPrice: null },
      detailModal: { show: false, item: null },
    }
  },
  computed: {
    dataList() { return this.$store.getters['bots/dataList'] },
    metaData() { return this.$store.getters['bots/metaData'] },
    positions() { return this.$store.getters['bots/positions'] },
    trades() { return this.$store.getters['bots/trades'] },
    wallets() { return this.$store.getters['bots/wallets'] },
    saveStatus() { return this.$store.getters['bots/dataSaveStatus'] },
    indicatorOptions() {
      const list = this.$store.getters['indicators/dataList']
      return [
        { value: null, text: '— seç —' },
        ...(list || []).map(i => ({ value: i.id, text: `${i.name} (${i.engineKey})` })),
      ]
    },
    targetOptions() {
      const list = this.$store.getters['targets/dataList']
      return [
        { value: null, text: '— yok (bağımsız bot) —' },
        ...(list || []).map(t => ({
          value: t.id,
          text: `${t.name} [${t.status}]`,
          disabled: t.status === 'COMPLETED' || t.status === 'FAILED',
        })),
      ]
    },
    apiKeyOptions() {
      const list = this.$store.getters['apiKeys/dataList']
      return [
        { value: null, text: '— yok (sadece TESTNET) —' },
        ...(list || []).filter(k => k.isActive).map(k => ({
          value: k.id,
          text: `${k.provider ? k.provider.name : '?'} · ${k.label || '(etiketsiz)'}`,
        })),
      ]
    },
    canSubmit() {
      const baseOk = this.form.name && this.form.indicatorId && this.form.symbol && this.form.timeframe
        && this.form.tpPerc > 0 && this.form.slPerc > 0 && this.form.leverage > 0
      if (!baseOk) return false
      if (this.form.mode === 'LIVE') {
        if (!this.form.apiKeyId) return false
        if (!this.form.capitalAllocated || this.form.capitalAllocated <= 0) return false
      }
      return true
    },
    canSubmitEdit() {
      const baseOk = this.editForm.name
        && this.editForm.tpPerc > 0 && this.editForm.slPerc > 0 && this.editForm.leverage > 0
      if (!baseOk) return false
      if (this.editModal.original && this.editModal.original.mode === 'LIVE') {
        if (!this.editForm.apiKeyId) return false
        if (!this.editForm.capitalAllocated || this.editForm.capitalAllocated <= 0) return false
      }
      return true
    },
    stepJsonError() {
      return this.validateStepJson(this.form.stepStopEnabled, this.form.stepStopJson)
    },
    editStepJsonError() {
      return this.validateStepJson(this.editForm.stepStopEnabled, this.editForm.stepStopJson)
    },
    canSubmitStops() {
      const tp = this.stopsForm.tpPrice
      const sl = this.stopsForm.slPrice
      const hasOne = (tp !== null && tp !== '' && Number(tp) > 0) || (sl !== null && sl !== '' && Number(sl) > 0)
      return hasOne
    },
    openPositions() { return this.positions.filter(p => p.status === 'OPEN') },
    openPositionsCount() { return this.openPositions.length },
    allPositions() { return this.positions },
  },
  watch: {
    saveStatus(val) {
      if (val.status === true) {
        this.showToast({
          variant: 'success', title: 'Başarılı', icon: 'CheckIcon', text: val.message,
        })
      } else if (val.status === false) {
        this.showToast({
          variant: 'danger', title: 'Hata', icon: 'XIcon', text: val.message,
        })
      }
    },
    'detailModal.show': function (val) {
      if (!val) this.unsubscribeBot()
    },
  },
  beforeDestroy() {
    this.unsubscribeBot()
  },
  created() {
    this.fetchList()
    if (!this.$store.getters['indicators/dataList'].length) {
      this.$store.dispatch('indicators/getDataList', {})
    }
    if (!this.$store.getters['targets/dataList'].length) {
      this.$store.dispatch('targets/getDataList', {})
    }
    if (!this.$store.getters['apiKeys/dataList'].length) {
      this.$store.dispatch('apiKeys/getDataList', {})
    }
  },
  methods: {
    async fetchList() {
      this.loading = true
      await this.$store.dispatch('bots/getDataList', this.filter)
      this.loading = false
    },
    onSearchInput() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.fetchList(), 400)
    },
    openCreateModal() {
      this.createModal.show = true
    },
    async onSubmit() {
      try {
        const payload = this.buildBotPayload(this.form, /* isCreate */ true)
        if (payload === null) return
        await this.$store.dispatch('bots/create', payload)
        this.createModal.show = false
        this.resetForm()
      } catch (e) { /* toast already shown */ }
    },
    resetForm() {
      this.form = {
        name: '',
        mode: 'TESTNET',
        market: 'FUTURES',
        indicatorId: null,
        targetId: null,
        apiKeyId: null,
        symbol: 'BTCUSDT',
        timeframe: '5m',
        tpPerc: 0.5,
        slPerc: 0.3,
        leverage: 10,
        commissionPerSide: 0.05,
        capitalAllocated: 1000,
        trailStopEnabled: false,
        trail: { triggerProfit: 0.5, trailPercent: 0.3 },
        stepStopEnabled: false,
        stepStopJson: '',
        positionManagement: this.defaultPositionManagement(),
      }
    },
    openEditModal(item) {
      this.editModal.original = item
      const trailCfg = item.trailStopConfig || {}
      const stepCfg = item.stepStopConfig || null
      const pm = this.normalizePositionManagement(item.meta && item.meta.positionManagement)
      const itemMeta = item.meta || {}
      const hasLegacyOverride = Number(itemMeta.tpPercOverride || 0) > 0 || Number(itemMeta.slPercOverride || 0) > 0
      this.editForm = {
        name: item.name || '',
        symbol: item.symbol || '',
        timeframe: item.timeframe || '5m',
        tpPerc: Number(item.tpPerc) || 0,
        slPerc: Number(item.slPerc) || 0,
        leverage: Number(item.leverage) || 1,
        commissionPerSide: Number(item.commissionPerSide) || 0,
        capitalAllocated: Number(item.capitalAllocated) || 0,
        targetId: item.id_targets || null,
        apiKeyId: item.id_user_api_keys || null,
        trailStopEnabled: !!item.trailStopEnabled,
        trail: {
          triggerProfit: Number(trailCfg.triggerProfit) || 0.5,
          trailPercent: Number(trailCfg.trailPercent) || 0.3,
        },
        stepStopEnabled: !!item.stepStopEnabled,
        stepStopJson: stepCfg && stepCfg.steps ? JSON.stringify(stepCfg.steps, null, 2) : '',
        positionManagement: pm,
        inheritEdgeTpSl: itemMeta.inheritEdgeTpSl === undefined ? !hasLegacyOverride : itemMeta.inheritEdgeTpSl !== false,
        exitOnIndicatorSignal: itemMeta.exitOnIndicatorSignal !== false,
      }
      this.editModal.show = true
    },
    async onUpdate() {
      try {
        const { id } = this.editModal.original
        const payload = this.buildBotPayload(this.editForm, /* isCreate */ false)
        if (payload === null) return
        await this.$store.dispatch('bots/update', { id, body: payload })
        this.editModal.show = false
        this.editModal.original = null
      } catch (e) { /* toast already shown */ }
    },

    // ─── Build bot payload (create/edit) ─────────────────────────────────
    buildBotPayload(src, isCreate) {
      const out = {
        name: src.name,
        tpPerc: src.tpPerc,
        slPerc: src.slPerc,
        leverage: src.leverage,
        commissionPerSide: src.commissionPerSide,
        capitalAllocated: src.capitalAllocated,
        symbol: src.symbol,
        timeframe: src.timeframe,
        targetId: src.targetId || null,
        apiKeyId: src.apiKeyId || null,
        inheritEdgeTpSl: src.inheritEdgeTpSl !== false,
        exitOnIndicatorSignal: src.exitOnIndicatorSignal !== false,
        trailStopEnabled: !!src.trailStopEnabled,
        trailStopConfig: src.trailStopEnabled
          ? { triggerProfit: Number(src.trail.triggerProfit) || 0, trailPercent: Number(src.trail.trailPercent) || 0 }
          : null,
        stepStopEnabled: !!src.stepStopEnabled,
        stepStopConfig: null,
        positionManagement: this.normalizePositionManagement(src.positionManagement),
      }
      if (src.stepStopEnabled) {
        try {
          const arr = JSON.parse(src.stepStopJson || '[]')
          if (!Array.isArray(arr)) throw new Error('array bekleniyor')
          out.stepStopConfig = { steps: arr }
        } catch (e) {
          this.showToast({
            variant: 'danger', title: 'Hata', icon: 'XIcon', text: `Steps JSON hatalı: ${e.message}`,
          })
          return null
        }
      }
      if (isCreate) {
        out.mode = src.mode
        out.market = src.market
        out.indicatorId = src.indicatorId
      }
      return out
    },

    defaultPositionManagement() {
      return {
        breakEven: {
          enabled: false, triggerProfit: 0.35, bufferProfit: 0.05, includeFees: true, bufferMode: 'NET', slippageBufferPct: 0.05,
        },
        partialTp: {
          enabled: false, atProfit: 0.5, takePercent: 40, moveRemainderToBreakEven: true,
        },
        trailTp: { enabled: false, triggerProfit: 0.5, trailPercent: 0.35 },
        riskSizing: { enabled: false, riskUsdtPerTrade: 50, maxCapitalUsagePercent: 100 },
        dailyGuard: {
          enabled: false, maxDailyLossUsdt: 150, maxConsecutiveLosses: 2, cooldownMinutes: 60,
        },
        dailyProfitGuard: { enabled: false, maxDailyProfitUsdt: 100 },
        globalExposure: { enabled: true, blockOppositeSide: true, scope: 'GLOBAL' },
      }
    },
    normalizePositionManagement(value) {
      const d = this.defaultPositionManagement()
      const v = value || {}
      return {
        breakEven: { ...d.breakEven, ...(v.breakEven || {}) },
        partialTp: { ...d.partialTp, ...(v.partialTp || {}) },
        trailTp: { ...d.trailTp, ...(v.trailTp || {}) },
        riskSizing: { ...d.riskSizing, ...(v.riskSizing || {}) },
        dailyGuard: { ...d.dailyGuard, ...(v.dailyGuard || {}) },
        dailyProfitGuard: { ...d.dailyProfitGuard, ...(v.dailyProfitGuard || {}) },
        globalExposure: { ...d.globalExposure, ...(v.globalExposure || {}) },
      }
    },
    hasPositionManagementEnabled(pm) {
      const v = pm || {}
      return !!(
        v.breakEven?.enabled
        || v.partialTp?.enabled
        || v.trailTp?.enabled
        || v.riskSizing?.enabled
        || v.dailyGuard?.enabled
        || v.dailyProfitGuard?.enabled
        || v.globalExposure?.enabled
      )
    },

    // ─── Step JSON validator (UI badge) ──────────────────────────────────
    validateStepJson(enabled, json) {
      if (!enabled) return ''
      if (!json || !json.trim()) return 'Steps JSON boş olamaz'
      try {
        const arr = JSON.parse(json)
        if (!Array.isArray(arr)) return 'Array bekleniyor'
        for (const s of arr) {
          if (typeof s.atProfit !== 'number' || typeof s.moveStopTo !== 'number') {
            return 'Her step için atProfit + moveStopTo (number) zorunlu'
          }
        }
        return ''
      } catch (e) {
        return `JSON parse hatası: ${e.message}`
      }
    },

    // ─── Position actions ────────────────────────────────────────────────
    openStopsModal(pos) {
      this.stopsModal.position = pos
      this.stopsForm = {
        tpPrice: pos.tpPrice !== null && pos.tpPrice !== undefined ? Number(pos.tpPrice) : null,
        slPrice: pos.slPrice !== null && pos.slPrice !== undefined ? Number(pos.slPrice) : null,
      }
      this.stopsModal.show = true
    },
    async onSubmitStops() {
      if (!this.stopsModal.position) return
      const body = {}
      if (this.stopsForm.tpPrice !== null && this.stopsForm.tpPrice !== '' && Number(this.stopsForm.tpPrice) > 0) {
        body.tpPrice = Number(this.stopsForm.tpPrice)
      }
      if (this.stopsForm.slPrice !== null && this.stopsForm.slPrice !== '' && Number(this.stopsForm.slPrice) > 0) {
        body.slPrice = Number(this.stopsForm.slPrice)
      }
      if (!Object.keys(body).length) return
      await this.$store.dispatch('bots/updatePositionStops', {
        id: this.detailModal.item.id,
        positionId: this.stopsModal.position.id,
        body,
      })
      this.stopsModal.show = false
      this.stopsModal.position = null
    },
    onClosePosition(pos) {
      this.$swal({
        title: 'Pozisyon kapatılsın mı?',
        text: 'Güncel piyasa fiyatında manuel olarak kapatılacak.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Kapat',
        cancelButtonText: 'İptal',
        customClass: { confirmButton: 'btn btn-warning', cancelButton: 'btn btn-secondary ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (!result.value) return
        await this.$store.dispatch('bots/closePosition', {
          id: this.detailModal.item.id,
          positionId: pos.id,
        })
      })
    },
    async onStart(id) { await this.$store.dispatch('bots/start', id) },
    async onStop(id) { await this.$store.dispatch('bots/stop', id) },
    async onRemove(id) {
      this.$swal({
        title: 'Bot silinsin mi?',
        text: 'Çalışıyorsa önce durdurulur, sonra soft-delete edilir.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sil',
        cancelButtonText: 'İptal',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) await this.$store.dispatch('bots/remove', id)
      })
    },
    async onShowDetail(item) {
      this.detailModal.show = true
      this.detailModal.item = item
      await this.$store.dispatch('bots/getPositions', { id: item.id })
      await this.$store.dispatch('bots/getTrades', { id: item.id, limit: 100 })
      await this.$store.dispatch('bots/getWallets', { id: item.id, limit: 100 })
      this.subscribeBot(item.id)
    },
    subscribeBot(botId) {
      const sock = getSocket()
      if (!sock) return
      this.unsubscribeBot() // önceki abonelik varsa temizle
      this._botSubId = botId
      sock.emit('bot:subscribe', botId)
      this._botListeners = {
        positionOpen: p => {
          if (p && (!this.detailModal.item || p.symbol)) {
            this.$store.dispatch('bots/getPositions', { id: botId })
          }
        },
        positionClose: () => {
          this.$store.dispatch('bots/getPositions', { id: botId })
          this.$store.dispatch('bots/getTrades', { id: botId, limit: 100 })
          this.fetchList()
        },
        trade: () => {
          this.$store.dispatch('bots/getTrades', { id: botId, limit: 100 })
        },
        statusChange: () => {
          this.fetchList()
        },
      }
      sock.on('bot:position-open', this._botListeners.positionOpen)
      sock.on('bot:position-close', this._botListeners.positionClose)
      sock.on('bot:trade', this._botListeners.trade)
      sock.on('bot:status-change', this._botListeners.statusChange)
    },
    unsubscribeBot() {
      const sock = getSocket()
      if (!sock || !this._botListeners) return
      sock.off('bot:position-open', this._botListeners.positionOpen)
      sock.off('bot:position-close', this._botListeners.positionClose)
      sock.off('bot:trade', this._botListeners.trade)
      sock.off('bot:status-change', this._botListeners.statusChange)
      if (this._botSubId) sock.emit('bot:unsubscribe', this._botSubId)
      this._botSubId = null
      this._botListeners = null
    },

    // ─── Format helpers ───────────────────────────────────────────────────
    formatNum(v, d = 2) {
      if (v === null || v === undefined) return '—'
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(d) : '—'
    },
    formatPrice(v) {
      if (v === null || v === undefined) return '—'
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      let d = 4
      if (n >= 1000) d = 2; else if (n < 1) d = 6
      return n.toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d })
    },
    formatDateTime(v) {
      if (!v) return '—'
      try {
        const d = new Date(v)
        if (isNaN(d.getTime())) return String(v)
        return d.toLocaleString('tr-TR', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        })
      } catch (e) { return String(v) }
    },
    protectiveWarningText(position) {
      const warning = position && position.meta && position.meta.protectiveOrderWarning
      if (!warning) return ''
      const labels = {
        protective_setup_cleanup_cancel_failed: 'Protective kurulum temizliği başarısız',
        sibling_cancel_failed: 'Kardeş protective order iptal edilemedi',
        old_sl_cancel_failed: 'Eski SL order iptal edilemedi',
        manual_close_cancel_failed: 'Manuel kapama sonrası protective order iptali başarısız',
        boot_sibling_cancel_failed: 'Boot sync kardeş protective order iptal edemedi',
      }
      const failures = Array.isArray(warning.failures) ? warning.failures : []
      const orderIds = failures.length
        ? failures.map(f => f && f.orderId).filter(Boolean).join(', ')
        : [warning.orderId, warning.oldSlOrderId, warning.newSlOrderId].filter(Boolean).join(', ')
      const errors = failures.length
        ? failures.map(f => f && f.error).filter(Boolean).join(' | ')
        : warning.error
      return [
        labels[warning.type] || warning.type || 'Protective order uyarısı',
        orderIds ? `Order: ${orderIds}` : null,
        warning.at ? `Zaman: ${this.formatDateTime(warning.at)}` : null,
        errors ? `Hata: ${errors}` : null,
      ].filter(Boolean).join(' · ')
    },
    truncate(s, n) { return s && s.length > n ? `${s.slice(0, n)}...` : s },

    // ─── Badge variants ───────────────────────────────────────────────────
    statusVariant(s) {
      const map = {
        IDLE: 'secondary', RUNNING: 'success', PAUSED: 'warning', STOPPED: 'dark', ERROR: 'danger',
      }
      return map[s] || 'secondary'
    },
    modeVariant(m) {
      return m === 'LIVE' ? 'danger' : 'info'
    },

    showToast(params) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: params.title, icon: params.icon, text: params.text, variant: params.variant,
        },
      })
    },
  },
}
</script>
